import { chainIdToNativeCurrencyName, SupportedChainId } from 'config/chains'
import { PROMOTE_TOKENS_BY_CHAIN } from 'constants/tokens'
import { useMemo } from 'react'

import { isAddress } from '../utils'
import { useAllTokens } from './Tokens'
import { useConnection } from './useConnection'

/**
 * useResolveCurrencyAddress returns the resolved address (or native symbol) for a given currencyId.
 * It checks the current chain’s native symbol, a promoted token, direct addresses, and known token symbols.
 * Supports 'ETH' as an alias for the chain’s native currency. Returns undefined until tokens are loaded.
 */
export const useResolveCurrencyAddress = (currencyId?: string | null) => {
  const { chainId } = useConnection()
  const allTokens = useAllTokens()

  return useMemo<string | undefined>(() => {
    if (!currencyId) return undefined

    const nativeSymbol = chainId != null ? chainIdToNativeCurrencyName[chainId as SupportedChainId] : undefined

    const upper = currencyId.toUpperCase()

    if (upper === nativeSymbol || upper === 'ETH') return nativeSymbol
    if (isAddress(currencyId)) return currencyId

    const lower = currencyId.toLowerCase() // moved up
    const promoteToken = chainId != null ? PROMOTE_TOKENS_BY_CHAIN[chainId] : undefined

    if (promoteToken?.symbol && lower === promoteToken.symbol.toLowerCase()) {
      return promoteToken.address
    }

    const symbolToAddress = Object.values(allTokens).find((t) => t.symbol.toLowerCase() === lower)

    return symbolToAddress?.address ?? currencyId
  }, [currencyId, chainId, allTokens])
}
